import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Button, Chip, Alert, MenuItem
} from '@mui/material';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { IMAGING_STATUS_CONFIG } from '../models/imagingModel';

const MONTHS = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' },
];

function toDate(v) {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  return new Date(v);
}

export default function ImagingReportsHistory() {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [ordersMap, setOrdersMap] = useState({});
  const [loading, setLoading] = useState(true);
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [resSnap, ordSnap] = await Promise.all([
          getDocs(collection(db, 'imagingResults')),
          getDocs(collection(db, 'imagingOrders')),
        ]);
        setResults(resSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setOrdersMap(Object.fromEntries(ordSnap.docs.map(d => [d.id, { id: d.id, ...d.data() }])));
      } catch (e) {
        console.error('Error cargando informes:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    return results.filter(r => {
      const reportedAt = toDate(r.reportedAt) || toDate(r.approvedAt);
      if (!reportedAt) return false;
      return reportedAt.getFullYear() === year && reportedAt.getMonth() + 1 === month;
    });
  }, [results, year, month]);

  return (
    <Box sx={{ p: 3, bgcolor: '#F4F6F8', minHeight: '100vh' }}>
      <Typography variant="h4" fontWeight={700} mb={2}>Historial de informes</Typography>

      <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #E2E8F0', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            select
            size="small"
            label="Mes"
            value={month}
            onChange={e => setMonth(Number(e.target.value))}
            sx={{ minWidth: 140 }}
          >
            {MONTHS.map(m => (
              <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Año"
            type="number"
            value={year}
            onChange={e => setYear(Number(e.target.value))}
            sx={{ minWidth: 100 }}
          />
          <Typography variant="body2" color="text.secondary">
            {filtered.length} informe{filtered.length !== 1 ? 's' : ''} encontrado{filtered.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : filtered.length === 0 ? (
        <Alert severity="info">No hay informes para el mes seleccionado.</Alert>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, border: '1px solid #E2E8F0' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha informe</TableCell>
                <TableCell>Paciente</TableCell>
                <TableCell>Estudio(s)</TableCell>
                <TableCell>Médico solicitante</TableCell>
                <TableCell>Radiólogo</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Acción</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(r => {
                const reportedAt = toDate(r.reportedAt) || toDate(r.approvedAt);
                const order = ordersMap[r.orderId] || {};
                const status = order.status || 'reported';
                const cfg = IMAGING_STATUS_CONFIG[status] || {};
                const studies = (order.studiesRequested || []).map(s => s.examTypeName).join(', ');
                return (
                  <TableRow key={r.id}>
                    <TableCell>{reportedAt?.toLocaleDateString('es-CL') || '—'}</TableCell>
                    <TableCell>{r.patientName || order.patientName || '—'}</TableCell>
                    <TableCell>{studies || '—'}</TableCell>
                    <TableCell>{order.vetName || '—'}</TableCell>
                    <TableCell>{r.radiologistName || '—'}</TableCell>
                    <TableCell>
                      <Chip label={cfg.label || status} size="small" sx={{ bgcolor: cfg.bg, color: cfg.color, fontSize: '0.7rem', fontWeight: 700 }} />
                    </TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => navigate(`/imaging/orders/${r.orderId}`)}>Ver orden</Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
